<template>
  <div class="vue-circular-progress">
    <div
      class="circle"
      :style="{ maxHeight: `${circleSize}px` }"
    >
      <svg
        :width="circleSize"
        :height="circleSize"
        class="circle__svg"
      >
        <circle
          :cx="centralP"
          :cy="centralP"
          :r="radius"
          :style="{
            'stroke-width': strokeWidth,
            stroke: backgroundColor,
            strokeOpacity: backgroundOpacity,
          }"
          class="circle__progress circle__progress--path"
        />
        <circle
          :cx="centralP"
          :cy="centralP"
          :r="radius"
          :style="fileStyl"
          class="circle__progress circle__progress--fill"
        />
      </svg>

      <div
        v-if="showPercentage"
        class="percent"
      >
        <slot>
          <span class="percent__int">{{ int }}</span>
          <span class="dot">.</span>
          <span class="percent__dec">{{ dec }}</span>
          <span class="percent_sign">%</span>
        </slot>
      </div>
    </div>
    <slot name="footer" />
  </div>
</template>
<script>
import styleVariables from '@/assets/scss/variables.module.scss';

export default {
  name: 'CircularProgress',
  props: {
    strokeWidth: {
      type: Number,
      default: 4,
    },
    radius: {
      type: Number,
      default: 16,
    },
    transitionDuration: {
      type: Number,
      default: 1000,
    },
    backgroundOpacity: {
      type: Number,
      default: 1,
    },
    allowZeroValue: {
      type: Boolean,
      default: false,
    },
    value: {
      // should be a number and less or equal than 100
      validator(value) {
        return !Number.isNaN(Number(value)) && Number(value) <= 100;
      },
      default: '0.0',
    },
    showPercentage: {
      type: Boolean,
      default: false,
    },
    // this will reset the circle to 0 and it should be use for static value
    resetOnNewValue: {
      type: Boolean,
      default: false,
    },
    colorVariation: {
      type: String,
      default: 'blue',
      required: false,
    },
  },
  data() {
    return {
      offset: '',
      int: 0,
      dec: '00',
    };
  },
  computed: {
    strokeColor() {
      const strokeColorsMap = {
        gray: {
          dark: '#FFFFFF',
          light: styleVariables.gray900,
        },
        blue: {
          default: styleVariables.stBlue,
        },
        orange: {
          default: styleVariables.warning500,
        },
        red: {
          default: styleVariables.error500,
        },
      };

      return (
        strokeColorsMap[this.colorVariation]?.[this.theme] ||
        strokeColorsMap[this.colorVariation].default ||
        ''
      );
    },
    theme() {
      return this.$store.getters.getTheme;
    },
    backgroundColor() {
      const backgroundColorsMap = {
        gray: {
          dark: styleVariables.gray600,
          light: styleVariables.gray300,
        },
        blue: {
          default: styleVariables.primary50,
        },
        orange: {
          default: styleVariables.warning200,
        },
        red: {
          default: styleVariables.warning200,
        },
      };

      return (
        backgroundColorsMap[this.colorVariation]?.[this.theme] ||
        backgroundColorsMap[this.colorVariation].default ||
        ''
      );
    },
    circumference() {
      return this.radius * Math.PI * 2;
    },
    fileStyl() {
      return {
        strokeDashoffset: this.offset,
        '--initialStroke': this.circumference,
        '--transitionDuration': `${this.transitionDuration}ms`,
        'stroke-width': this.strokeWidth,
        stroke: this.strokeColor,
      };
    },
    circleSize() {
      return (this.radius + this.strokeWidth) * 2;
    },
    centralP() {
      return this.circleSize / 2;
    },
  },
  watch: {
    value: {
      handler(v) {
        const n = Number(v);
        if (Number.isNaN(n) || (n === 0 && !this.allowZeroValue)) {
          return;
        }
        this.clearHandlers();
        this.countNumber(v);
      },
      immediate: true,
    },
  },
  beforeUnmount() {
    this.clearHandlers();
  },
  methods: {
    increaseNumber(number, className) {
      if (number === 0) {
        return;
      }
      const innerNum = parseInt(this.findClosestNumber(this.transitionDuration / 10, number), 10);
      const interval = this.transitionDuration / innerNum;
      let counter = 0;
      const handlerName = `${className}Interval`;
      this[handlerName] = setInterval(() => {
        const bitDiff = number.toString().length - innerNum.toString().length;
        if (bitDiff === 0) {
          this[className] = counter;
        } else {
          this[className] = counter * 10 * bitDiff;
        }
        if (counter === innerNum) {
          // back to origin precision
          this[className] = number;
          window.clearInterval(this[handlerName]);
        }
        counter += 1;
      }, interval);
    },
    findClosestNumber(bound, value) {
      if (value <= bound) {
        return value;
      }
      return this.findClosestNumber(bound, value / 10);
    },
    countNumber(v) {
      if (this.resetOnNewValue) {
        this.offset = '';
      }
      requestAnimationFrame(() => {
        this.offset = (this.circumference * (100 - v)) / 100;
      });
      if (!this.showPercentage) return;
      if (this.$slots.default) return;
      let [int, dec] = v.toString().split('.');
      // fallback for NaN
      [int, dec] = [Number(int), Number(dec)];
      this.increaseNumber(int, 'int');
      this.increaseNumber(Number.isNaN(dec) ? 0 : dec, 'dec');
    },
    clearHandlers() {
      if (this.initTimeoutHandler) {
        clearTimeout(this.initTimeoutHandler);
      }
      if (this.intInterval) {
        clearInterval(this.intInterval);
      }
      if (this.decInterval) {
        clearInterval(this.decInterval);
      }
    },
  },
};
</script>
